import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errMsg } from '../../lib/api';
import PhrasePicker from '../PhrasePicker';
import type { MemorialData, MediaItem } from '../../hooks/useMemorialData';

/** 可配置区块：相册 / 留言墙 / 追思文章 */
export const ALL_BLOCKS = ['album', 'tributes', 'articles'] as const;
export const BLOCK_LABELS: Record<string, string> = {
  album: '追忆相册',
  tributes: '追思留言',
  articles: '追思文章',
};
export const DEFAULT_BLOCKS: string[] = ['album', 'tributes', 'articles'];

/** 解析模板 config JSON → 要渲染的区块列表（无效值自动过滤） */
export function resolveBlocks(config?: string | null): string[] {
  if (!config) return DEFAULT_BLOCKS;
  try {
    const parsed = JSON.parse(config) as { blocks?: unknown };
    if (!Array.isArray(parsed.blocks)) return DEFAULT_BLOCKS;
    return parsed.blocks.filter(
      (b): b is string => typeof b === 'string' && (ALL_BLOCKS as readonly string[]).includes(b)
    );
  } catch {
    return DEFAULT_BLOCKS;
  }
}

function fmtDate(ts: number) {
  return new Date(ts * 1000).toLocaleDateString('zh-CN');
}

function SectionTitle({ children }: { children: string }) {
  return (
    <h2 className="mb-5 text-center text-xs tracking-[0.2em] text-[var(--c-muted)]">
      {children}
    </h2>
  );
}

// ============ 追忆相册 ============
function AlbumBlock({ media }: { media: MediaItem[] }) {
  if (media.length === 0) return null;
  return (
    <section className="mt-14">
      <SectionTitle>相 册</SectionTitle>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
        {media.map((m) => (
          <div
            key={m.id}
            className="aspect-square overflow-hidden rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)]"
          >
            {m.type === 'video' ? (
              <video
                src={m.url}
                className="h-full w-full object-cover"
                controls
                preload="metadata"
                playsInline
              />
            ) : (
              <img src={m.url} alt={m.caption ?? ''} className="h-full w-full object-cover" />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ============ 追思留言（留言墙） ============
interface TributeItem {
  id: string;
  type: string;
  fromName: string;
  message: string | null;
  createdAt: number;
}

function TributesBlock({
  memorialId,
  canEdit,
}: {
  memorialId: string;
  canEdit: boolean;
}) {
  const [items, setItems] = useState<TributeItem[]>([]);
  const [name, setName] = useState('');
  const [msg, setMsg] = useState('');
  const [anon, setAnon] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    const res = await api<{ items: TributeItem[] }>(`/tributes/memorial/${memorialId}`);
    if (res.ok && res.data) setItems(res.data.items);
  }

  useEffect(() => {
    load();
  }, [memorialId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!msg.trim()) {
      setErr('请写下想说的话');
      return;
    }
    setBusy(true);
    setErr('');
    const res = await api('/tributes', {
      method: 'POST',
      body: JSON.stringify({
        memorialId,
        type: 'message',
        visitorName: name.trim() || undefined,
        message: msg.trim(),
        isAnonymous: anon,
      }),
    });
    setBusy(false);
    if (!res.ok) {
      setErr(errMsg(res, '发送失败'));
      return;
    }
    setMsg('');
    load();
  }

  async function remove(id: string) {
    if (!window.confirm('确定删除这条留言？')) return;
    const res = await api(`/tributes/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      setErr(errMsg(res, '删除失败'));
      return;
    }
    load();
  }

  return (
    <section className="mt-14">
      <SectionTitle>追 思 留 言</SectionTitle>

      <form onSubmit={submit} className="mb-6 rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface)] p-4">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="你的称呼（可留空）"
            className="min-w-[120px] flex-1 rounded-lg border border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-1.5 text-sm outline-none"
          />
          <label className="flex items-center gap-1.5 text-xs text-[var(--c-muted)]">
            <input
              type="checkbox"
              checked={anon}
              onChange={(e) => setAnon(e.target.checked)}
            />
            匿名
          </label>
        </div>
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={3}
          placeholder="写下你想说的话…"
          className="w-full rounded-lg border border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-2 text-sm outline-none"
        />
        <PhrasePicker onPick={(t) => setMsg(t)} />
        {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
        <button
          type="submit"
          disabled={busy}
          className="mt-2 rounded-full bg-[var(--c-text)] px-4 py-1.5 text-xs text-[var(--c-bg)] disabled:opacity-50"
        >
          {busy ? '发送中…' : '发表留言'}
        </button>
      </form>

      {items.length === 0 ? (
        <p className="text-center text-sm text-[var(--c-muted)]">还没有人留言，来说点什么吧</p>
      ) : (
        <ul className="space-y-3">
          {items.map((t) => (
            <li
              key={t.id}
              className="rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] p-3"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs text-[var(--c-accent)]">{t.fromName}</span>
                <span className="text-[10px] text-[var(--c-muted)]">{fmtDate(t.createdAt)}</span>
              </div>
              <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-[var(--c-text)]">
                {t.message}
              </p>
              {canEdit && (
                <button
                  onClick={() => remove(t.id)}
                  className="mt-2 text-[10px] text-red-600 underline"
                >
                  删除
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ============ 追思文章 ============
interface ArticleItem {
  id: string;
  title: string;
  excerpt: string;
  createdAt: number;
}

function ArticlesBlock({
  memorialId,
  memorialSlug,
}: {
  memorialId: string;
  memorialSlug: string;
}) {
  const [items, setItems] = useState<ArticleItem[]>([]);

  useEffect(() => {
    api<{ items: ArticleItem[] }>(`/articles?memorialId=${memorialId}`).then((res) => {
      if (res.ok && res.data) setItems(res.data.items);
    });
  }, [memorialId]);

  if (items.length === 0) return null;

  return (
    <section className="mt-14">
      <SectionTitle>追 思 文 章</SectionTitle>
      <ul className="space-y-3">
        {items.map((a) => (
          <li key={a.id}>
            <Link
              to={`/m/${memorialSlug}/articles/${a.id}`}
              className="block rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] p-4 transition hover:border-[var(--c-accent)]"
            >
              <p className="font-serifcn text-base text-[var(--c-text)]">{a.title}</p>
              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[var(--c-muted)]">
                {a.excerpt}
              </p>
              <p className="mt-2 text-[10px] text-[var(--c-muted)]">{fmtDate(a.createdAt)}</p>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ============ 区块容器：按配置顺序渲染 ============
export default function ContentBlocks({
  memorial,
  media,
  blocks,
  memorialSlug,
}: {
  memorial: MemorialData;
  media: MediaItem[];
  blocks?: string[];
  memorialSlug: string;
}) {
  const active = blocks ?? DEFAULT_BLOCKS;
  if (active.length === 0) return null;

  return (
    <>
      {active.map((b) => {
        if (b === 'album') return <AlbumBlock key="album" media={media} />;
        if (b === 'tributes')
          return (
            <TributesBlock key="tributes" memorialId={memorial.id} canEdit={Boolean(memorial.canEdit)} />
          );
        if (b === 'articles')
          return (
            <ArticlesBlock key="articles" memorialId={memorial.id} memorialSlug={memorialSlug} />
          );
        return null;
      })}
    </>
  );
}
